import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { Sheet, SheetContent } from '@/components/ui/sheet'
import { cn } from '@/libs/utils'

interface DetailSheetProps {
  children: ReactNode
  className?: string
}

/**
 * Wraps a details page so it can be reached from a row click on its listing
 * page as a right-side quick view, while the list stays mounted behind it.
 * Rendered by the "background location" route tree in App.tsx — see the
 * `detailRoutes` config there — matched against the real URL while the
 * primary <Routes> tree keeps matching the list page's (background) URL.
 *
 * Closing (X, overlay click, Esc, or the details page's own back button,
 * since that already calls `navigate(-1)`/back to its list) goes back one
 * history entry, returning to the list exactly as it was before the row was
 * clicked. Direct navigation to a details URL (no background location set —
 * a bookmark, a page refresh, or the listing page's "view details" quick
 * action) never reaches this wrapper at all; it renders as a full page via
 * the existing nested route instead.
 */
export function DetailSheet({ children, className }: DetailSheetProps) {
  const navigate = useNavigate()

  return (
    <Sheet open onOpenChange={(open) => !open && navigate(-1)}>
      <SheetContent
        className={cn(
          'w-full sm:max-w-3xl overflow-y-auto p-0 [scrollbar-gutter:stable]',
          className,
        )}
      >
        {/* SheetContent's close (X) button is `absolute right-4 top-4` with a 16px
            (h-4 w-4) icon, so its box always ends 32px below the panel's top edge —
            pt-12 (48px) reserves a header band that clears it with a 16px cushion on
            every page here, since each wraps its own content in `page-wrap` (min
            p-4 = 16px on mobile), and even a page with zero top padding of its own
            would still clear it (48px > 32px). Horizontal/bottom padding is
            intentionally left to each page's own `page-wrap` class, since adding it
            here would double it up for every page that already self-pads.
            `scrollbar-gutter:stable` reserves the vertical scrollbar's track
            up front, so the visible left/right balance of that padding doesn't
            shift depending on whether this panel's content is currently tall
            enough to scroll. */}
        <div className="pt-12">{children}</div>
      </SheetContent>
    </Sheet>
  )
}
